/* ============================================================
   JERKIN' IT v2.0 — STORE
   Cart drawer state machine, one-more-bag picker, box builder.
   Shopify remains the pricing authority: discounted prices and savings
   are read from Shopify's cart response, never estimated by the theme.
   ============================================================ */
(() => {
  'use strict';

  const { money, toast, track, escapeHtml, Drawer, q, qa } = window.JI;
  const root = () => (window.JI?.routes?.root || '/').replace(/\/$/, '');
  const offers = () => ({
    one: Number(window.JI?.offers?.tierOneQty || 4),
    two: Number(window.JI?.offers?.tierTwoQty || 8),
    p1: Number(window.JI?.offers?.tierOnePercent || 10),
    p2: Number(window.JI?.offers?.tierTwoPercent || 15)
  });

  let lastCount = null;
  let lastMilestoneHead = null;
  let milestoneUserSet = false;
  let milestoneCollapseTimer = null;

  /* ---------- Cart API ---------- */
  async function getCart() {
    const r = await fetch(`${root()}/cart.js`, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('Could not reach your bag. Try again.');
    return r.json();
  }
  async function mutate(path, payload) {
    const r = await fetch(`${root()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!r.ok) {
      let msg = 'Could not update your bag.';
      try { msg = (await r.json()).description || msg; } catch (e) { /* keep default */ }
      throw new Error(msg);
    }
    return r.json();
  }
  async function addItems(items) {
    const data = await mutate('/cart/add.js', { items });
    document.dispatchEvent(new CustomEvent('ji:cart:added'));
    return data;
  }
  async function changeLine(key, quantity) {
    return mutate('/cart/change.js', { id: key, quantity });
  }

  /* ============================================================
     MILESTONE COPY — exact states, per the approved spec.
     Discount information is never replaced by an Easter egg.
     ============================================================ */
  function milestone(units) {
    const { one, two, p1, p2 } = offers();
    const state = { head: '', sub: '', pct: 0, unlocked: false, showPicker: false };

    if (units <= 0) {
      state.head = 'Pick a bag. Regret nothing.';
      return state;
    }
    if (units < one) {
      const left = one - units;
      state.pct = (units / one) * 100;
      if (left === 1) {
        state.head = 'ONE MORE.';
        state.sub = `Then ${p1}% off.`;
        state.showPicker = true;
      } else if (units === 1) {
        state.head = 'Good start.';
        state.sub = `${left} more bags unlock ${p1}%.`;
      } else {
        state.head = 'Halfway to sensible.';
        state.sub = `${left} more bags unlock ${p1}%.`;
      }
      return state;
    }
    if (units === one) {
      state.head = `${one}-BAG TIER REACHED.`;
      state.sub = `${two - one} more bags reach the ${p2}% tier. Applied savings appear below.`;
      state.pct = (units / two) * 100;
      state.unlocked = true;
      return state;
    }
    if (units < two) {
      const left = two - units;
      state.pct = (units / two) * 100;
      state.unlocked = true;
      if (left === 1) {
        state.head = 'ONE MORE BAG.';
        state.sub = `REACHES THE ${p2}% TIER.`;
        state.showPicker = true;
      } else {
        state.head = `${p1}% TIER REACHED.`;
        state.sub = `${left} more bags reach the ${p2}% tier. Applied savings appear below.`;
      }
      return state;
    }
    state.head = `${p2}% TIER REACHED.`;
    state.sub = 'Applied savings appear below when Shopify confirms them.';
    state.pct = 100;
    state.unlocked = true;
    return state;
  }

  // Secondary microcopy only. Never replaces the discount line above.
  function egg(units) {
    if (units === 4) return 'Good on ya. First tier reached.';
    if (units === 6) return 'This escalated.';
    if (units === 8) return 'Beauty. Top tier reached.';
    if (units === 9) return 'We respect the commitment.';
    if (units === 12) return 'Do you own a cupboard?';
    if (units === 14) return 'That should get you to smoko.';
    if (units >= 16) return 'Planning something?';
    return '';
  }

  /* ============================================================
     ONE-MORE-BAG PICKER
     Only ever suggests flavours that are NOT already in the bag.
     ============================================================ */
  async function suggestions(cart, limit = 3) {
    const inCart = new Set(cart.items.map(i => i.product_id));
    const seen = new Set();
    const out = [];

    const consider = (list) => {
      (list || []).forEach(p => {
        if (out.length >= limit) return;
        if (!p || inCart.has(p.id) || seen.has(p.id)) return;
        const v = (p.variants || []).find(x => x.available);
        if (!v) return;
        seen.add(p.id);
        out.push({ id: v.id, title: p.title, price: p.price, image: p.featured_image || p.image || '' });
      });
    };

    // 1. The canonical collection, rendered into the drawer by Liquid.
    const pool = q('[data-ji-cart-pool]');
    if (pool) {
      try { consider(JSON.parse(pool.textContent || '[]')); } catch (e) { /* ignore malformed pool */ }
    }
    // 2. Shopify recommendations, seeded from each cart line, as a backstop.
    if (out.length < limit && cart.items.length) {
      for (const item of cart.items.slice(0, 2)) {
        if (out.length >= limit) break;
        try {
          const r = await fetch(`${root()}/recommendations/products.json?product_id=${item.product_id}&limit=6&intent=related`, { headers: { Accept: 'application/json' } });
          if (r.ok) consider((await r.json()).products);
        } catch (e) { /* network hiccup: fall through */ }
      }
    }
    return out;
  }

  /* ---------- Drawer rendering ---------- */
  const drawerEl = q('[data-ji-cart-drawer]');
  const cartDrawer = drawerEl ? new Drawer(drawerEl, { overlay: q('[data-ji-cart-overlay]'), onOpen: render }) : null;

  function lineItemHtml(item) {
    const originalLinePrice = Number(item.original_line_price || 0);
    const finalLinePrice = Number(item.final_line_price || 0);
    const hasDiscount = originalLinePrice > finalLinePrice;
    return `
      <div class="ji-cart-item" data-line-key="${item.key}">
        <img class="ji-cart-item__image" src="${item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : ''}" alt="" loading="lazy">
        <div>
          <div class="ji-cart-item__title">${escapeHtml(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="ji-cart-item__meta">${escapeHtml(item.variant_title)}</div>` : ''}
          <div class="ji-stepper">
            <button type="button" aria-label="Decrease ${escapeHtml(item.product_title)} quantity" data-cart-line="${item.key}" data-cart-qty="${item.quantity - 1}">−</button>
            <span aria-hidden="true">${item.quantity}</span>
            <button type="button" aria-label="Increase ${escapeHtml(item.product_title)} quantity" data-cart-line="${item.key}" data-cart-qty="${item.quantity + 1}">+</button>
          </div>
          <button type="button" class="ji-cart-remove" data-cart-line="${item.key}" data-cart-qty="0">Remove</button>
        </div>
        <div class="ji-cart-item__price">
          ${hasDiscount ? `<s>${money(originalLinePrice)}</s>` : ''}
          <strong>${money(finalLinePrice)}</strong>
        </div>
      </div>`;
  }

  function discountTitles(cart) {
    const titles = new Set();
    const addTitle = application => {
      const title = application?.title || application?.discount_application?.title;
      if (title) titles.add(String(title));
    };

    (cart.cart_level_discount_applications || []).forEach(addTitle);
    (cart.discount_applications || []).forEach(addTitle);
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(addTitle);
    });
    return [...titles];
  }

  function renderSavings(cart) {
    const savings = q('[data-ji-cart-savings]', drawerEl);
    if (!savings) return;

    const totalDiscount = Math.max(0, Number(cart.total_discount || 0));
    savings.hidden = !totalDiscount;
    if (!totalDiscount) return;

    q('[data-ji-cart-savings-total]', savings)?.replaceChildren(document.createTextNode(`-${money(totalDiscount)}`));
    const list = q('[data-ji-cart-discount-list]', savings);
    if (list) {
      list.innerHTML = discountTitles(cart)
        .map(title => `<li>${escapeHtml(title)}</li>`)
        .join('');
      list.hidden = !list.childElementCount;
    }
  }

  function renderShippingGoal(cart) {
    const goal = q('[data-ji-shipping-goal]', drawerEl);
    if (!goal) return;

    const threshold = Number(goal.dataset.thresholdCents || 0);
    const enabled = goal.dataset.enabled === 'true';
    const cartTotal = Math.max(0, Number(cart.total_price || 0));
    const canShow = enabled && threshold > 0 && cart.item_count > 0 && cart.requires_shipping !== false;
    goal.hidden = !canShow;
    if (!canShow) return;

    const remaining = Math.max(0, threshold - cartTotal);
    const unlocked = remaining === 0;
    const pct = Math.min(100, Math.round((cartTotal / threshold) * 100));
    const awayCopy = goal.dataset.awayCopy || '[amount] away from free shipping.';
    const unlockedCopy = goal.dataset.unlockedCopy || 'Free shipping unlocked.';
    const message = unlocked ? unlockedCopy : awayCopy.replace('[amount]', money(remaining));
    q('[data-ji-shipping-message]', goal)?.replaceChildren(document.createTextNode(message));
    const bar = q('[data-ji-shipping-progress]', goal);
    if (bar) bar.style.width = `${pct}%`;
    const track = q('[data-ji-shipping-progress-track]', goal);
    track?.setAttribute('aria-valuenow', String(pct));
    goal.classList.toggle('is-unlocked', unlocked);
  }

  async function render() {
    if (!drawerEl) return;
    let cart;
    try {
      cart = await getCart();
    } catch (err) {
      const errEl = q('[data-ji-cart-error]', drawerEl);
      if (errEl) errEl.textContent = err.message;
      return;
    }

    const errEl = q('[data-ji-cart-error]', drawerEl);
    if (errEl) errEl.textContent = '';

    const items = q('[data-ji-cart-items]', drawerEl);
    if (items) {
      items.innerHTML = cart.items.length
        ? cart.items.map(lineItemHtml).join('')
        : `<div class="ji-cart__empty"><strong>Empty.</strong><span>That feels fixable.</span><a class="ji-button" href="${q('[data-ji-cart-shop-url]')?.dataset.jiCartShopUrl || root() + '/collections/all'}">Shop the good stuff</a></div>`;
    }

    // Milestone
    const m = milestone(cart.item_count);
    const wrap = q('[data-ji-milestone]', drawerEl);
    const head = q('[data-ji-milestone-head]', drawerEl);
    const sub = q('[data-ji-milestone-sub]', drawerEl);
    const eggEl = q('[data-ji-milestone-egg]', drawerEl);
    const bar = q('[data-ji-cart-progress]', drawerEl);
    if (head) head.textContent = m.head;
    if (sub) sub.textContent = m.sub;
    if (eggEl) eggEl.textContent = egg(cart.item_count);
    if (bar) bar.style.width = Math.min(100, m.pct) + '%';
    wrap?.classList.toggle('is-unlocked', m.unlocked);

    // Auto-minimise once a message has had a moment to be read, so the footer
    // stays short enough for Checkout to stay in view. A genuine change (new
    // tier, new copy) always re-opens it; a manual toggle pauses the timer.
    const milestoneToggle = q('[data-ji-milestone-toggle]', drawerEl);
    if (milestoneToggle) milestoneToggle.hidden = cart.item_count === 0;
    if (wrap) {
      const changed = m.head !== lastMilestoneHead;
      lastMilestoneHead = m.head;
      clearTimeout(milestoneCollapseTimer);
      if (changed) {
        wrap.classList.remove('is-collapsed');
        milestoneToggle?.setAttribute('aria-expanded', 'true');
        milestoneUserSet = false;
      }
      if (!milestoneUserSet && cart.item_count > 0) {
        milestoneCollapseTimer = setTimeout(() => {
          wrap.classList.add('is-collapsed');
          milestoneToggle?.setAttribute('aria-expanded', 'false');
        }, 2600);
      }
    }

    // Totals
    q('[data-ji-cart-subtotal]', drawerEl)?.replaceChildren(document.createTextNode(money(cart.total_price)));
    renderSavings(cart);
    renderShippingGoal(cart);
    qa('[data-ji-cart-count]').forEach(el => { el.textContent = String(cart.item_count); });
    const checkout = q('[data-ji-cart-checkout]', drawerEl);
    if (checkout) checkout.disabled = !cart.item_count;

    renderPicker(cart, m);
    reportTransition(cart.item_count);
  }

  async function renderPicker(cart, m) {
    const box = q('[data-ji-onemore]', drawerEl);
    if (!box) return;
    if (!m.showPicker) { box.hidden = true; return; }
    const list = await suggestions(cart, 2);
    if (!list.length) { box.hidden = true; return; }
    box.hidden = false;
    const row = q('[data-ji-onemore-row]', box);
    if (row) {
      row.innerHTML = list.map(p => `
        <button type="button" class="ji-upsell" data-ji-add data-variant="${p.id}" data-title="${escapeHtml(p.title)}">
          ${p.image ? `<img src="${p.image.replace(/(\.[a-z]+)(\?|$)/i, '_80x$1$2')}" alt="" loading="lazy">` : ''}
          <span class="ji-upsell__body"><strong>${escapeHtml(p.title)}</strong><small>${money(p.price)}</small></span>
          <b>Add +</b>
        </button>`).join('');
    }
    track('cart_upsell_shown', { count: list.length, cart_units: cart.item_count });
  }

  function reportTransition(count) {
    if (lastCount === null) { lastCount = count; return; }
    if (count === lastCount) return;
    const { one, two } = offers();
    if (lastCount < count) {
      if (lastCount === 1 && count === 2) track('cart_quantity_1_to_2');
      if (lastCount === 2 && count === 3) track('cart_quantity_2_to_3');
      if (lastCount === 3 && count === 4) track('cart_quantity_3_to_4');
      if (lastCount < one && count >= one) track('discount_tier_reached', { tier: 1, quantity: count });
      if (lastCount < two && count >= two) track('discount_tier_reached', { tier: 2, quantity: count });
    }
    lastCount = count;
  }

  /* ---------- Events ---------- */
  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-ji-cart-open]')) { e.preventDefault(); cartDrawer?.open(); return; }
    if (e.target.closest('[data-ji-cart-close]')) { e.preventDefault(); cartDrawer?.close(); return; }
    if (e.target.matches('[data-ji-cart-overlay]')) { cartDrawer?.close(); return; }

    const milestoneToggle = e.target.closest('[data-ji-milestone-toggle]');
    if (milestoneToggle) {
      e.preventDefault();
      milestoneUserSet = true;
      clearTimeout(milestoneCollapseTimer);
      const wrap = milestoneToggle.closest('[data-ji-milestone]');
      const collapsed = wrap?.classList.toggle('is-collapsed');
      milestoneToggle.setAttribute('aria-expanded', String(!collapsed));
      return;
    }

    const line = e.target.closest('[data-cart-line]');
    if (line) {
      line.disabled = true;
      try {
        await changeLine(line.dataset.cartLine, Number(line.dataset.cartQty));
        await render();
      } catch (err) {
        const errEl = q('[data-ji-cart-error]', drawerEl);
        if (errEl) errEl.textContent = err.message; else toast(err.message);
        line.disabled = false;
      }
      return;
    }

    const add = e.target.closest('[data-ji-add]');
    if (add) {
      e.preventDefault();
      const id = Number(add.dataset.variant);
      if (!id || add.disabled) return;
      const isQuickAddButton = add.matches('button.ji-quick-add');
      const original = add.innerHTML;
      add.disabled = true;
      add.classList.add('is-adding');
      try {
        await addItems([{ id, quantity: Number(add.dataset.qty || 1) }]);
        add.classList.add('is-added');
        if (isQuickAddButton) add.innerHTML = '<span>Added</span><b aria-hidden="true">✓</b>';
        track('quick_add', { variant_id: id, title: add.dataset.title || '' });
        cartDrawer?.open();
        await render();
      } catch (err) {
        toast(err.message);
      } finally {
        setTimeout(() => {
          add.disabled = false;
          add.classList.remove('is-adding', 'is-added');
          add.innerHTML = original;
        }, 1100);
      }
    }
  });

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-ji-product-form]');
    if (!form) return;
    e.preventDefault();
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    const quantity = Number(fd.get('quantity') || 1);
    const btn = form.querySelector('[type="submit"]');
    const label = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    try {
      await addItems([{ id, quantity }]);
      track('pdp_add', { variant_id: id, quantity });
      cartDrawer?.open();
      await render();
    } catch (err) {
      toast(err.message);
    } finally {
      if (btn) setTimeout(() => { btn.disabled = false; btn.textContent = label; }, 900);
    }
  });

  document.addEventListener('ji:cart:added', render);
  document.addEventListener('ji:cart:refresh', render);
  qa('[data-ji-cart-checkout]').forEach(b => b.addEventListener('click', () => track('begin_checkout')));

  /* ============================================================
     BUILD A BOX
     Real Shopify line items. Never computes a discounted price.
     ============================================================ */
  class JIBox extends HTMLElement {
    connectedCallback() {
      this.size = Number(this.dataset.defaultSize || 4);
      this.slots = [];
      this.started = false;
      this.slotWrap = q('[data-box-slots]', this);
      this.msg = q('[data-box-message]', this);
      this.count = q('[data-box-count]', this);
      this.protein = q('[data-box-protein]', this);
      this.bar = q('[data-box-bar]', this);
      this.cta = q('[data-box-add]', this);

      qa('[data-box-tier]', this).forEach(btn => btn.addEventListener('click', () => {
        const next = Number(btn.dataset.boxTier);
        if (next === this.size) return;
        // Selections are preserved. Shrinking keeps the first N rather than wiping.
        if (next < this.size) this.slots = this.slots.slice(0, next);
        this.size = next;
        qa('[data-box-tier]', this).forEach(x => {
          const on = x === btn;
          x.classList.toggle('is-active', on);
          x.setAttribute('aria-pressed', String(on));
        });
        this.render();
      }));

      qa('[data-box-pick]', this).forEach(btn => btn.addEventListener('click', () => {
        if (this.slots.length >= this.size) { toast('Box is full. Remove one first, or go up to 8.'); return; }
        this.slots.push({
          id: Number(btn.dataset.variant),
          name: btn.dataset.name,
          accent: btn.dataset.accent,
          image: btn.dataset.image || '',
          protein: Number(btn.dataset.protein || 0)
        });
        if (!this.started) { this.started = true; track('build_box_started', { size: this.size }); }
        track('build_box_slot_filled', { filled: this.slots.length, size: this.size });
        this.render();
      }));

      this.cta?.addEventListener('click', () => this.submit());
      this.render();
    }

    async submit() {
      if (this.slots.length !== this.size) return;
      const counts = {};
      this.slots.forEach(s => { counts[s.id] = (counts[s.id] || 0) + 1; });
      this.cta.disabled = true;
      const label = this.cta.textContent;
      this.cta.textContent = 'Adding…';
      try {
        await addItems(Object.entries(counts).map(([id, quantity]) => ({ id: Number(id), quantity })));
        track('build_box_completed', { size: this.size, unique: Object.keys(counts).length });
        this.slots = [];
        this.started = false;
        this.render();
        document.dispatchEvent(new CustomEvent('ji:cart:refresh'));
        q('[data-ji-cart-open]')?.click();
      } catch (err) {
        toast(err.message);
      } finally {
        this.cta.disabled = false;
        this.cta.textContent = label;
      }
    }

    render() {
      if (!this.slotWrap) return;
      const filled = this.slots.length;
      const remaining = this.size - filled;
      const pct = Math.round((filled / this.size) * 100);
      const percent = this.size >= Number(window.JI?.offers?.tierTwoQty || 8)
        ? Number(window.JI?.offers?.tierTwoPercent || 15)
        : Number(window.JI?.offers?.tierOnePercent || 10);

      this.slotWrap.style.setProperty('--slot-cols', Math.min(this.size, 4));
      this.slotWrap.innerHTML = Array.from({ length: this.size }, (_, i) => {
        const s = this.slots[i];
        return `<div class="ji-slot${s ? ' is-filled' : ''}"${s?.accent ? ` style="--slot-accent:${s.accent}"` : ''}>
          <span class="ji-slot__n">${String(i + 1).padStart(2, '0')}</span>
          ${s?.image ? `<img class="ji-slot__image" src="${s.image}" alt="" loading="lazy">` : ''}
          <span class="ji-slot__name">${s ? escapeHtml(s.name) : '+'}</span>
          ${s ? `<button type="button" class="ji-slot__remove" data-remove="${i}" aria-label="Remove ${escapeHtml(s.name)} from slot ${i + 1}">×</button>` : ''}
        </div>`;
      }).join('');

      qa('[data-remove]', this.slotWrap).forEach(b => b.addEventListener('click', () => {
        this.slots.splice(Number(b.dataset.remove), 1);
        this.render();
      }));

      if (this.bar) this.bar.style.width = pct + '%';
      if (this.count) this.count.textContent = `${filled} / ${this.size} selected`;
      if (this.protein) {
        const hasProteinForEveryBag = filled > 0 && this.slots.every(s => Number.isFinite(s.protein) && s.protein > 0);
        const proteinTotal = Math.round(this.slots.reduce((sum, s) => sum + (s.protein || 0), 0) * 10) / 10;
        this.protein.hidden = !hasProteinForEveryBag;
        this.protein.textContent = hasProteinForEveryBag
          ? `≈${proteinTotal}g protein packed across ${filled} ${filled === 1 ? 'bag' : 'bags'} — not a serving suggestion.`
          : '';
      }
      if (this.msg) {
        this.msg.innerHTML = remaining
          ? `${remaining === 1 ? 'One more bag.' : `${remaining} more bags.`}<br><em>Then the ${percent}% tier is ready.</em>`
          : `Nice.<br><em>Your ${percent}% tier is ready. Shopify confirms the saving in the bag and at checkout.</em>`;
      }
      this.classList.toggle('is-complete', remaining === 0 && filled > 0);
      if (this.cta) {
        this.cta.disabled = remaining !== 0;
        this.cta.textContent = remaining === 0 ? `Add box — ${percent}% tier` : `Add box (${filled}/${this.size})`;
      }
    }
  }
  if (!customElements.get('ji-box-builder')) customElements.define('ji-box-builder', JIBox);

  /* ---------- Sticky buy bar ---------- */
  const sticky = q('[data-sticky-buy]');
  const pForm = q('[data-ji-product-form]');
  if (sticky && pForm && window.JI?.ux?.stickyBuy !== false) {
    new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-visible', !entry.isIntersecting);
    }, { threshold: 0.15 }).observe(pForm);
  }

  window.JIStore = { addItems, getCart, changeLine, render, money, milestone };
})();
